<template>
  <div class="image-viewer">
    <!-- 遮罩层 -->
    <div class="overlay"></div>

    <!-- 图片容器 -->
    <div
      @click.stop="closeViewer"
      class="image-container"
      :style="{
        transform: `scale(${scale}) rotate(${rotate}deg) translate(${offsetX}px, ${offsetY}px)`,
        transition: transitionEnabled ? 'transform 0.3s ease' : 'none',
      }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseUp"
    >
      <img :src="currentImage" alt="" />
    </div>

    <!-- 控制按钮 -->
    <div class="controls">
      <button @click.stop="scaleImage(1)">缩小</button>
      <button @click.stop="resetState">重置</button>
      <button @click.stop="rotateLeft">逆时针旋转</button>
      <button>{{ currentIndex + 1 }}/{{ images.length }}</button>
      <button @click.stop="rotateRight">顺时针旋转</button>
      <button @click.stop="closeViewer">关闭</button>
      <button @click.stop="scaleImage(2)">放大</button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    images: {
      type: Array,
      required: true,
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      currentIndex: this.initialIndex,
      scale: 1,
      rotate: 0,
      offsetX: 0,
      offsetY: 0,
      transitionEnabled: false,
      touchStart: { x: 0, y: 0, time: 0 },
      initialDistance: null,
      lastScale: 1,
      isSuoFang: false,
      // 新增的长按拖动相关状态
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 },
      longPressTimer: null,
    };
  },
  computed: {
    currentImage() {
      return this.images[this.currentIndex];
    },
  },
  methods: {
    // 处理鼠标按下事件（用于PC端长按拖动）
    handleMouseDown(e) {
      // 阻止默认行为，防止图片被选中
      e.preventDefault();
      
      // 清除之前的定时器
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
      }
      
      // 设置长按定时器
      this.longPressTimer = setTimeout(() => {
        if (this.scale > 1) { // 只在放大状态下允许拖动
          this.isDragging = true;
          this.dragStart = {
            x: e.clientX,
            y: e.clientY
          };
          this.dragOffset = {
            x: this.offsetX,
            y: this.offsetY
          };
          this.transitionEnabled = false;
        }
      }, 300); // 300毫秒后触发长按
    },
    
    // 处理鼠标移动事件（用于PC端拖动）
    handleMouseMove(e) {
      if (this.isDragging) {
        const deltaX = e.clientX - this.dragStart.x;
        const deltaY = e.clientY - this.dragStart.y;
        
        this.offsetX = this.dragOffset.x + deltaX;
        this.offsetY = this.dragOffset.y + deltaY;
      }
    },
    
    // 处理鼠标释放事件（用于PC端结束拖动）
    handleMouseUp() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.isDragging = false;
    },
    
    // 处理触摸开始
    handleTouchStart(e) {
      this.transitionEnabled = false;
      const touches = e.touches;

      if (touches.length === 2) {
        // 双指缩放
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        this.initialDistance = Math.sqrt(dx * dx + dy * dy);
        this.lastScale = this.scale;
        this.isSuoFang = true;
      } else if (touches.length === 1) {
        // 单指滑动或长按拖动
        this.touchStart = {
          x: touches[0].clientX,
          y: touches[0].clientY,
          time: Date.now(),
        };
        this.isSuoFang = false;
        
        // 设置长按定时器
        this.longPressTimer = setTimeout(() => {
          if (this.scale > 1) { // 只在放大状态下允许拖动
            this.isDragging = true;
            this.dragStart = {
              x: touches[0].clientX,
              y: touches[0].clientY
            };
            this.dragOffset = {
              x: this.offsetX,
              y: this.offsetY
            };
          }
        }, 200); // 300毫秒后触发长按
      }
    },
    // 处理触摸移动
    handleTouchMove(e) {
      const touches = e.touches;

      if (touches.length === 2 && this.initialDistance) {
        // 双指缩放
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const newScale = (distance / this.initialDistance) * this.lastScale;
        this.scale = Math.min(Math.max(newScale, 0.5), 4); // 限制缩放范围
        this.isSuoFang = true;
        
        // 缩放时取消长按
        if (this.longPressTimer) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }
      } else if (touches.length === 1) {
        if (this.isDragging) {
          // 长按拖动状态
          const deltaX = touches[0].clientX - this.dragStart.x;
          const deltaY = touches[0].clientY - this.dragStart.y;
          
          this.offsetX = this.dragOffset.x + deltaX;
          this.offsetY = this.dragOffset.y + deltaY;
        } else if (this.scale === 1) {
          // 单指滑动切换
          const deltaX = touches[0].clientX - this.touchStart.x;
          const deltaY = touches[0].clientY - this.touchStart.y;

          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            e.preventDefault(); // 阻止默认滚动行为
            this.offsetX = deltaX;
          }
          this.isSuoFang = false;
          
          // 滑动时取消长按
          if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
          }
        }
      }
    },
    scaleImage(type) {
      if (type == 1) {
        if (this.scale > 0.5) {
          this.scale = this.scale - 0.5;
        }
      } else {
        this.scale = this.scale + 0.5;
      }
    },
    // 处理触摸结束
    handleTouchEnd(e) {
      // 清除长按定时器
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      
      const duration = Date.now() - this.touchStart.time;
      const deltaX = e.changedTouches[0].clientX - this.touchStart.x;

      // 判断是否快速滑动切换
      if (!this.isDragging && this.scale === 1 && Math.abs(deltaX) > 50 && duration < 300) {
        this.transitionEnabled = true;
        this.offsetX = deltaX > 0 ? window.innerWidth : -window.innerWidth;

        setTimeout(() => {
          this.currentIndex =
            deltaX > 0
              ? Math.max(0, this.currentIndex - 1)
              : Math.min(this.images.length - 1, this.currentIndex + 1);
          if (!this.isSuoFang) {
            this.resetState();
          }
        }, 0);
      } else {
        if (!this.isSuoFang && !this.isDragging) {
          this.resetState();
        }
      }

      this.initialDistance = null;
      this.isDragging = false;
    },
    // 重置状态
    resetState() {
      this.scale = 1;
      this.rotate = 0;
      this.offsetX = 0;
      this.offsetY = 0;
      this.transitionEnabled = false;
    },
    // 向左旋转
    rotateLeft() {
      this.rotate -= 90;
    },
    // 向右旋转
    rotateRight() {
      this.rotate += 90;
    },
    // 关闭查看器
    closeViewer() {
      this.$emit("close");
    },
  },
  watch: {
    currentIndex(newIndex) {
      console.log(newIndex);
      this.$emit("change-index", newIndex);
    },
  },
};
</script>

<style scoped>
.image-viewer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999999999999;
}

.overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
}

.image-container {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
}

img {
  max-width: 100%;
  max-height: 750px;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.controls {
  position: fixed;
  display: flex;
  gap: 0.13333rem;
  z-index: 1000;
  top: 0.25rem;
  text-align: center;
  display: flex;
  justify-content: center;
  width: 100%;
}

button {
  padding: 10px 20px;
  font-size: 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
</style>